import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { CheckSetup, MicrSymbols } from '@accounting/shared';
import { isValidRoutingNumber, MICR_SYMBOL_PRESETS } from '@accounting/shared';
import { StockLayoutPanel } from '../../components/checks/StockLayoutPanel';
import {
  useCheckSetups,
  useCheckStockLayout,
  useDeleteMicrFont,
  useDeleteSignature,
  useMicrFont,
  useUpdateCheckSetup,
  useOpenBlankChecks,
  usePrintBlankChecks,
  useUpdateMicrSymbols,
  useUploadMicrFont,
  useUploadSignature,
} from '../../hooks/useCheckPrinting';
import { ApiError, openFileInNewTab } from '../../lib/api';

export const Route = createFileRoute('/_auth/settings_/check-printing')({
  component: CheckPrintingSettingsPage,
});

function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return (err.body as { error?: string } | null)?.error ?? fallback;
  return err instanceof Error ? err.message : fallback;
}

/** Strip the `data:...;base64,` prefix a FileReader result carries. */
function toBase64(dataUrl: string): string {
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(toBase64(String(reader.result)));
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsDataURL(file);
  });
}

function CheckPrintingSettingsPage() {
  const { data: setups = [], isLoading } = useCheckSetups();
  const { data: stock } = useCheckStockLayout();
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Link to="/settings" style={{ fontSize: 13 }}>← Settings</Link>
        <h1 style={{ fontSize: 24, margin: '8px 0 4px' }}>Check printing</h1>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          What prints on the checks. An account appears here once you turn on{' '}
          <strong>Writes checks</strong> for it on the{' '}
          <Link to="/accounts">Chart of Accounts</Link>.
        </p>
      </div>

      <MicrFontPanel />

      {stock && <StockLayoutPanel status={stock} />}

      {isLoading && <p className="muted">Loading accounts…</p>}

      <div className="card" style={{ padding: 16 }}>
        <h2 style={{ fontSize: 15, marginTop: 0 }}>Check-writing accounts</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Account</th>
              <th>Status</th>
              <th>Routing</th>
              <th>Account no.</th>
              <th style={{ textAlign: 'right' }}>Next no.</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {setups.map((s) => (
              <tr key={s.bankAccountId}>
                <td>{s.companyName}</td>
                <td className="muted">{s.accountCode} — {s.accountName}</td>
                <td>
                  {s.readyToPrint ? (
                    <span className="tag ok">ready</span>
                  ) : (
                    <span className="tag warn" title={s.missing.join(', ')}>
                      {s.missing.length} missing
                    </span>
                  )}
                </td>
                <td className="muted">{s.routingNumberMasked ?? '—'}</td>
                <td className="muted">{s.accountNumberLast4 ? `••••${s.accountNumberLast4}` : '—'}</td>
                <td style={{ textAlign: 'right' }}>{s.nextCheckNumber}</td>
                <td>
                  <button
                    className="btn-secondary"
                    onClick={() => setOpenId(openId === s.bankAccountId ? null : s.bankAccountId)}
                  >
                    {openId === s.bankAccountId ? 'Close' : 'Set up'}
                  </button>
                </td>
              </tr>
            ))}
            {setups.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="muted">
                  No account writes checks yet. Turn on <strong>Writes checks</strong> for a cash
                  account on the <Link to="/accounts">Chart of Accounts</Link> and it will appear
                  here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openId && (
        <SetupEditor
          setup={setups.find((s) => s.bankAccountId === openId)!}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

/**
 * The E-13B font is system-wide, not per account: it is a typeface, and the same
 * one serves every company's stock.
 */
function MicrFontPanel() {
  const { data: font } = useMicrFont();
  const upload = useUploadMicrFont();
  const remove = useDeleteMicrFont();

  return (
    <div
      className="card"
      style={{
        padding: 16,
        marginBottom: 16,
        borderLeft: `3px solid ${font?.installed ? 'var(--ok)' : 'var(--warn)'}`,
      }}
    >
      <h2 style={{ fontSize: 15, marginTop: 0 }}>MICR font (E-13B)</h2>
      {font?.installed ? (
        <p style={{ marginTop: 0, fontSize: 13 }}>
          <span className="tag ok">installed</span>{' '}
          <strong>{font.fileName}</strong>{' '}
          <span className="muted">
            · {((font.sizeBytes ?? 0) / 1024).toFixed(0)} KB · sha256 {font.sha256?.slice(0, 12)}…
          </span>
        </p>
      ) : (
        <p style={{ marginTop: 0, fontSize: 13 }}>
          <span className="tag warn">not installed</span> Checks cannot print until an E-13B font is
          uploaded — the bank line would come out blank and no bank could read the check.
        </p>
      )}
      <p className="muted" style={{ fontSize: 12 }}>
        E-13B faces are licensed per vendor, so the font is not shipped with the app — upload the
        one you have licensed. You also need MICR magnetic toner in the printer; a normal toner
        cartridge produces a line that scans visually but fails magnetic reading.
      </p>

      {font?.installed && <MicrSymbolPicker current={font.symbols} />}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <label className="btn-secondary" style={{ cursor: 'pointer' }}>
          {font?.installed ? 'Replace font' : 'Upload font'}
          <input
            type="file"
            accept=".ttf,.otf,font/ttf,font/otf"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              await upload.mutateAsync({
                fontBase64: await readFileAsBase64(file),
                fileName: file.name,
              });
              e.target.value = '';
            }}
          />
        </label>
        {font?.installed && (
          <button
            className="btn-danger"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm('Remove the MICR font? No checks can print until one is uploaded again.')) {
                remove.mutate();
              }
            }}
          >
            Remove
          </button>
        )}
      </div>
      {upload.error && (
        <p className="field-error" style={{ marginBottom: 0 }}>
          {errText(upload.error, 'Font upload failed.')}
        </p>
      )}
    </div>
  );
}

/**
 * Which characters carry E-13B's four symbols.
 *
 * Not detectable from the file: the same glyph sits on 'A' in one vendor's face
 * and 'T' in another's. Pick wrong and the MICR line prints readable letters
 * where the magnetic symbols belong — the check looks right and no bank can
 * read it. The calibration sheet prints these four on their own to check.
 */
function MicrSymbolPicker({ current }: { current: MicrSymbols | null }) {
  const save = useUpdateMicrSymbols();
  const active = current ?? MICR_SYMBOL_PRESETS.abcd.symbols;
  const matches = (s: MicrSymbols) =>
    s.transit === active.transit &&
    s.amount === active.amount &&
    s.onUs === active.onUs &&
    s.dash === active.dash;

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <h3 style={{ fontSize: 13, margin: '0 0 6px' }}>Symbol mapping</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(['abcd', 'toad'] as const).map((key) => {
          const preset = MICR_SYMBOL_PRESETS[key];
          const on = matches(preset.symbols);
          return (
            <label key={key} style={{ fontSize: 13 }}>
              <input
                type="radio"
                name="micr-symbols"
                checked={on}
                disabled={save.isPending}
                onChange={() => save.mutate(preset.symbols)}
              />{' '}
              {preset.label}
              {on && <span className="tag ok" style={{ marginLeft: 8 }}>in use</span>}
            </label>
          );
        })}
      </div>
      <p className="muted" style={{ fontSize: 11, marginBottom: 0 }}>
        Currently transit <code>{active.transit}</code>, amount <code>{active.amount}</code>, on-us{' '}
        <code>{active.onUs}</code>, dash <code>{active.dash}</code>. Print the calibration sheet — if
        any of the four prints as a readable letter, this is set wrong.
      </p>
      {save.error && (
        <p className="field-error" style={{ marginBottom: 0 }}>
          {errText(save.error, 'Could not save the mapping.')}
        </p>
      )}
    </div>
  );
}

function SetupEditor({ setup, onClose }: { setup: CheckSetup; onClose: () => void }) {
  const save = useUpdateCheckSetup();
  const uploadSig = useUploadSignature();
  const removeSig = useDeleteSignature();

  const [routing, setRouting] = useState('');
  const [account, setAccount] = useState('');
  const [bankName, setBankName] = useState(setup.bankName ?? '');
  const [bankAddress, setBankAddress] = useState(setup.bankAddress ?? '');
  const [fraction, setFraction] = useState(setup.fractionalRouting ?? '');
  const [nextNumber, setNextNumber] = useState(String(setup.nextCheckNumber));
  const [sigName, setSigName] = useState(setup.signatureName ?? '');
  const [offsetX, setOffsetX] = useState(setup.offsetXPt);
  const [offsetY, setOffsetY] = useState(setup.offsetYPt);

  useEffect(() => {
    setBankName(setup.bankName ?? '');
    setBankAddress(setup.bankAddress ?? '');
    setFraction(setup.fractionalRouting ?? '');
    setNextNumber(String(setup.nextCheckNumber));
    setSigName(setup.signatureName ?? '');
    setOffsetX(setup.offsetXPt);
    setOffsetY(setup.offsetYPt);
  }, [setup]);

  const routingBad = routing.length > 0 && !isValidRoutingNumber(routing);

  return (
    <div className="card" style={{ padding: 20, marginTop: 16, maxWidth: 720 }}>
      <h2 style={{ fontSize: 16, marginTop: 0 }}>
        {setup.companyName} — {setup.accountCode} {setup.accountName}
      </h2>

      {setup.missing.length > 0 && (
        <p className="field-error" style={{ fontSize: 13 }}>
          Still needed: {setup.missing.join(', ')}
        </p>
      )}

      <p className="muted" style={{ fontSize: 12, margin: '10px 0 14px' }}>
        This account writes checks. To stop that, turn off <strong>Writes checks</strong> for it on
        the <Link to="/accounts">Chart of Accounts</Link>.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Routing number" hint={setup.routingNumberMasked ? `stored: ${setup.routingNumberMasked}` : '9 digits'}>
          <input
            value={routing}
            onChange={(e) => setRouting(e.target.value.replace(/\D/g, '').slice(0, 9))}
            placeholder={setup.routingNumberMasked ? 'leave blank to keep' : '123456789'}
            inputMode="numeric"
          />
          {routingBad && (
            <span className="field-error" style={{ fontSize: 12 }}>
              Fails the ABA check digit — re-read it off a real check.
            </span>
          )}
        </Field>

        <Field
          label="Account number"
          hint={setup.accountNumberLast4 ? `stored: ••••${setup.accountNumberLast4}` : '4–17 digits'}
        >
          <input
            value={account}
            onChange={(e) => setAccount(e.target.value.replace(/\D/g, '').slice(0, 17))}
            placeholder={setup.accountNumberLast4 ? 'leave blank to keep' : '0012345678'}
            inputMode="numeric"
          />
        </Field>

        <Field label="Bank name (prints on the check)">
          <input value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="First National Bank" />
        </Field>

        <Field label="Bank address">
          <input value={bankAddress} onChange={(e) => setBankAddress(e.target.value)} />
        </Field>

        <Field label="Corner fraction" hint="cosmetic">
          <input value={fraction} onChange={(e) => setFraction(e.target.value)} placeholder="12-345/6780" />
        </Field>

        <Field label="Next check number" hint="cannot move below a number already used">
          <input
            value={nextNumber}
            onChange={(e) => setNextNumber(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
          />
        </Field>

        <Field label="Signature caption" hint="prints under the line">
          <input value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder="Kenneth Bateman, Manager" />
        </Field>
      </div>

      {/* ── Signature ─────────────────────────────────────────────────────── */}
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>Signature image</h3>
        <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
          Printed on every check from this account. Treat it as you would a signed blank check:
          anyone who can reach a generated PDF has a negotiable instrument. Stored encrypted and
          never sent back to the browser.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className={setup.hasSignatureImage ? 'tag ok' : 'tag warn'}>
            {setup.hasSignatureImage ? 'on file' : 'none'}
          </span>
          <label className="btn-secondary" style={{ cursor: 'pointer' }}>
            {setup.hasSignatureImage ? 'Replace' : 'Upload PNG'}
            <input
              type="file"
              accept="image/png"
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                await uploadSig.mutateAsync({
                  companyId: setup.companyId,
                  bankAccountId: setup.bankAccountId,
                  imageBase64: await readFileAsBase64(file),
                  fileName: file.name,
                });
                e.target.value = '';
              }}
            />
          </label>
          {setup.hasSignatureImage && (
            <button
              className="btn-danger"
              disabled={removeSig.isPending}
              onClick={() =>
                removeSig.mutate({ companyId: setup.companyId, bankAccountId: setup.bankAccountId })
              }
            >
              Remove
            </button>
          )}
        </div>
        {uploadSig.error && (
          <p className="field-error">{errText(uploadSig.error, 'Signature upload failed.')}</p>
        )}
      </div>

      {/* ── Calibration ───────────────────────────────────────────────────── */}
      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>Printer alignment</h3>
        <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
          Print the calibration sheet on plain paper and hold it against a real check. If the boxes
          sit low or left, nudge the offsets. 72 points = 1 inch, so 3.6 = 1/20". Then print sample
          checks to see the actual face — they carry a VOID stamp and a zeroed check number, so they
          are safe to run on real stock, but shred them afterwards. Print at 100% scale; any
          "fit to page" setting moves the MICR line out of tolerance.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="X offset (pt)">
            <input value={offsetX} onChange={(e) => setOffsetX(e.target.value)} style={{ width: 90 }} />
          </Field>
          <Field label="Y offset (pt)">
            <input value={offsetY} onChange={(e) => setOffsetY(e.target.value)} style={{ width: 90 }} />
          </Field>
          <button
            className="btn-secondary"
            onClick={() =>
              openFileInNewTab(
                `/companies/${setup.companyId}/check-setup/${setup.bankAccountId}/calibration.pdf`,
              )
            }
          >
            Print calibration sheet
          </button>
          {/* The real face, VOID-stamped: outlines tell you where the boxes are,
            * a sample check tells you whether the printing inside them fits. */}
          <button
            className="btn-secondary"
            onClick={() =>
              openFileInNewTab(
                `/companies/${setup.companyId}/check-setup/${setup.bankAccountId}/sample.pdf`,
              )
            }
          >
            Print sample checks
          </button>
        </div>
      </div>

      <BlankCheckPanel setup={setup} />

      {save.error && <p className="field-error">{errText(save.error, 'Could not save.')}</p>}

      <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
        <button
          className="btn-primary"
          disabled={save.isPending || routingBad}
          onClick={async () => {
            await save.mutateAsync({
              companyId: setup.companyId,
              bankAccountId: setup.bankAccountId,
              input: {
                // checkWritingEnabled is deliberately absent: the Chart of
                // Accounts toggle owns it, and sending it from here would give
                // one setting two writers.
                // Absent means "keep what is stored" — these never round-trip
                // to the browser, so sending an empty string would wipe them.
                routingNumber: routing ? routing : undefined,
                accountNumber: account ? account : undefined,
                bankName: bankName || null,
                bankAddress: bankAddress || null,
                fractionalRouting: fraction || null,
                nextCheckNumber: Number(nextNumber) || 1,
                signatureName: sigName || null,
                offsetXPt: Number(offsetX) || 0,
                offsetYPt: Number(offsetY) || 0,
              },
            });
            setRouting('');
            setAccount('');
          }}
        >
          Save
        </button>
        <button className="btn-secondary" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

/**
 * Numbered blanks to write by hand away from the office.
 *
 * These consume real check numbers the moment they print, because that is what
 * they are: usable checks on real stock with a real MICR line. They are never
 * pre-signed.
 */
function BlankCheckPanel({ setup }: { setup: CheckSetup }) {
  const print = usePrintBlankChecks();
  const { data: open = [] } = useOpenBlankChecks(setup.companyId);
  const [count, setCount] = useState('3');
  const [notes, setNotes] = useState('');
  const [issued, setIssued] = useState<string[] | null>(null);

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
      <h3 style={{ fontSize: 14, margin: '0 0 6px' }}>Blank checks for offsite use</h3>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        Prints numbered checks with everything except payee, date and amount, to fill in by hand.
        The numbers are consumed as soon as they print — these are real checks — so the register can
        account for them. They are never pre-signed: sign each one when you write it.
      </p>

      {open.length > 0 && (
        <p style={{ fontSize: 13 }}>
          <span className="tag warn">{open.length} still out</span>{' '}
          <span className="muted">
            #{open[0]!.checkNumber}
            {open.length > 1 ? `–${open[open.length - 1]!.checkNumber}` : ''} — record each one when
            it is written so the register closes.
          </span>
        </p>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Field label="How many">
          <input
            type="number"
            min={1}
            max={50}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            style={{ width: 80 }}
          />
        </Field>
        <Field label="Where they are going" hint="optional">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="job site folder"
            style={{ width: 220 }}
          />
        </Field>
        <button
          className="btn-secondary"
          disabled={print.isPending || !setup.readyToPrint}
          title={setup.readyToPrint ? undefined : 'Finish the setup above first'}
          onClick={async () => {
            const n = Number(count);
            if (!Number.isInteger(n) || n < 1) return;
            if (
              !confirm(
                `Print ${n} numbered blank check${n === 1 ? '' : 's'}? ` +
                  `${n} check number${n === 1 ? '' : 's'} will be used up and cannot be reused.`,
              )
            ) {
              return;
            }
            const res = await print.mutateAsync({
              companyId: setup.companyId,
              bankAccountId: setup.bankAccountId,
              count: n,
              notes: notes.trim() || undefined,
            });
            setIssued(res.numbers);
            window.open(res.objectUrl, '_blank', 'noopener,noreferrer');
          }}
        >
          Print blank checks
        </button>
      </div>

      {issued && (
        <p style={{ fontSize: 13, marginBottom: 0 }}>
          Issued #{issued[0]}{issued.length > 1 ? `–#${issued[issued.length - 1]}` : ''}. Record each
          one as a hand-written check when you use it.
        </p>
      )}
      {print.error && (
        <p className="field-error" style={{ marginBottom: 0 }}>
          {errText(print.error, 'Could not print blank checks.')}
        </p>
      )}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'block', fontSize: 13 }}>
      {label}
      {hint && <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>{hint}</span>}
      <div style={{ marginTop: 4 }}>{children}</div>
    </label>
  );
}
